import type {SafeHtml} from '@github-ui/dotcom-schema/types/common/v1/safe_html'
import type {PolymorphicComponent} from '@github-ui/react-polymorphic'
import {markUnsafeStaticHTML} from '@github-ui/trusted-types/unsafe-static'
import {Text, type TextProps} from '@primer/react'
import type React from 'react'

import type {SanitizedHTML, TrustedHTML} from './safe-html'

/**
 * Resolve the `html` prop to the value expected by `dangerouslySetInnerHTML`:
 * - a `SafeHTMLString` (branded string) is minted through the `github-unsafe-static`
 *   Trusted Types policy so the sink receives a real `TrustedHTML` under a
 *   `require-trusted-types-for 'script'` CSP (falls back to the string when the
 *   policy is inactive)
 * - a `SafeHtml` protobuf message contributes its `.value` string, minted the same way
 * - an incoming `TrustedHTML` (e.g. already minted by `github-sanitize`) is passed
 *   straight through and never re-minted
 *
 * `github-unsafe-static` is the sink policy for every verified-but-not-already-
 * `TrustedHTML` string here — static literals, server-origin strings, and
 * DOMPurify-sanitized fallback alike — not only static literals.
 */
function resolveSafeInnerHTML(html: SanitizedHTML | SafeHtml): string | TrustedHTML {
  if (typeof html === 'string') return markUnsafeStaticHTML(html)
  if ('value' in html) return markUnsafeStaticHTML(html.value)
  return html
}

interface VerifiedHTMLProps {
  /**
   * Set the rendered HTML of the component. To prevent XSS, ensure that the source of this
   * HTML is trusted! If the source is untrusted, use the `UnsafeHTML` component instead.
   */
  html?: SanitizedHTML | SafeHtml
}

type PropsWithHTML<T> = T & VerifiedHTMLProps

/**
 * `VerifiedHTMLText` extends `Text` from `@primer/react` to allow injecting HTML strings
 * into the DOM.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLText` component instead.
 */
export const VerifiedHTMLText = withSafeHTML<TextProps>(Text) as PolymorphicComponent<
  'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a' | 'td',
  PropsWithHTML<
    TextProps<'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a' | 'td'>
  >
>

type DivProps = VerifiedHTMLProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

const Div = ({ref, ...props}: DivProps & {ref?: React.Ref<HTMLDivElement | null>}) => <div {...props} ref={ref} />
Div.displayName = 'Div'

/**
 * `VerifiedHTMLDiv` extends `div` with props for safely rendering HTML strings.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLDiv` component instead.
 */
export const VerifiedHTMLDiv = withSafeHTML<DivProps>(Div)

type BdiProps = VerifiedHTMLProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>

const Bdi = ({ref, ...props}: BdiProps & {ref?: React.Ref<HTMLElement | null>}) => <bdi {...props} ref={ref} />
Bdi.displayName = 'Bdi'

/**
 * `VerifiedHTMLBdi` extends `bdi` with props for safely rendering HTML strings.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLBdi` component instead.
 */
export const VerifiedHTMLBdi = withSafeHTML<BdiProps, HTMLElement>(Bdi)
/**
 * A higher-order component that extends a basic component to inject an
 * `html` prop into the DOM via `dangerouslySetInnerHTML`.
 */
function withSafeHTML<T, R = Element>(Component: React.ComponentType<T & {ref?: React.Ref<R>}>) {
  const SafeHTMLComponent = ({html, ...props}: PropsWithHTML<T & {ref?: React.Ref<R>}>) => {
    return (
      <Component
        {...(props as T & {ref?: React.Ref<R>})}
        // `suppressHydrationWarning`: on the server `__html` is a string, on the client it's `TrustedHTML`, but both render identical DOM output
        suppressHydrationWarning
        // eslint-disable-next-line react/forbid-component-props -- safe-html is the only place in the codebase where `dangerouslySetInnerHTML` should be allowed
        dangerouslySetInnerHTML={html ? {__html: resolveSafeInnerHTML(html)} : undefined}
      />
    )
  }
  SafeHTMLComponent.displayName = `SafeHTML${Component.displayName || Component.name}`

  return SafeHTMLComponent
}
